import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useMultiTenant } from './useMultiTenant';
import { useAuth } from '@/contexts/AuthContext';
import { auditService } from '@/services/auditService';
import type { 
  DeliveryWithDetails, 
  CreateDeliveryData,
  DeliveryStatus,
} from '@/types/delivery';

export const useDeliveryMutations = () => {
  const { toast } = useToast();
  const { companyId } = useMultiTenant();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Create delivery — §I.37: delivery_number vem do documento de origem (obrigatório)
  const createDelivery = useMutation({
    mutationFn: async (deliveryData: CreateDeliveryData) => {
      if (!deliveryData.delivery_number?.trim()) {
        throw new Error('Número da entrega é obrigatório (deve espelhar o documento de origem).');
      }

      const { items, ...deliveryFields } = deliveryData;

      const { data: delivery, error: deliveryError } = await supabase
        .from('deliveries')
        .insert({
          company_id: companyId,
          ...deliveryFields,
        })
        .select()
        .single();

      if (deliveryError) throw deliveryError;

      if (items.length > 0) {
        const { error: itemsError } = await supabase
          .from('delivery_items')
          .insert(
            items.map(item => ({
              delivery_id: delivery.id,
              company_id: companyId,
              ...item,
            }))
          );

        if (itemsError) throw itemsError;
      }

      await supabase.from('delivery_history').insert({
        delivery_id: delivery.id,
        action: 'CREATED',
        new_status: 'pending',
        changed_by: user?.id,
      });

      await auditService.logAction({
        action: 'CRIAÇÃO',
        module: 'LOGÍSTICA',
        entityType: 'ENTREGA',
        entityId: delivery.id,
        description: `Entrega ${delivery.delivery_number} criada`,
        details: {
          delivery_number: delivery.delivery_number,
          origin_type: deliveryData.origin_type,
          priority: deliveryData.priority,
          items_count: deliveryData.items.length,
        },
      });

      return delivery;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliveries'] });
      toast({
        title: 'Entrega criada',
        description: 'A entrega foi criada com sucesso.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao criar entrega',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Assign delivery to technician
  const assignDelivery = useMutation({
    mutationFn: async ({ deliveryId, technicianUserId, instructions, sendNotification = true }: { 
      deliveryId: string; 
      technicianUserId: string;
      instructions?: string;
      sendNotification?: boolean;
    }) => {
      const { data: deliveryBefore } = await supabase
        .from('deliveries')
        .select(`
          delivery_address,
          contact_name,
          status,
          sale:sales(customer:customers(name))
        `)
        .eq('id', deliveryId)
        .single();

      const previousStatus = deliveryBefore?.status || 'pending';

      const { data, error } = await supabase
        .from('deliveries')
        .update({
          assigned_to: technicianUserId,
          assigned_at: new Date().toISOString(),
          status: 'assigned',
          delivery_instructions: instructions || undefined,
        })
        .eq('id', deliveryId)
        .select()
        .single();

      if (error) throw error;

      await supabase.from('delivery_history').insert({
        delivery_id: deliveryId,
        action: 'ASSIGNED',
        previous_status: previousStatus,
        new_status: 'assigned',
        changed_by: user?.id,
        notes: `Atribuída ao técnico logístico`,
      });

      await auditService.logAction({
        action: 'ATUALIZAÇÃO',
        module: 'LOGÍSTICA',
        entityType: 'ENTREGA',
        entityId: deliveryId,
        description: `Entrega atribuída a técnico`,
        details: {
          technician_id: technicianUserId,
          status: 'assigned',
        },
      });

      if (sendNotification) {
        try {
          const customerName = deliveryBefore?.sale?.customer?.name || deliveryBefore?.contact_name || 'Cliente';
          const address = deliveryBefore?.delivery_address || 'Endereço não informado';
          
          await supabase.functions.invoke('send-push-notification', {
            body: {
              user_id: technicianUserId,
              title: '🚚 Nova Entrega Atribuída',
              message: `Entrega para ${customerName} em ${address}`,
              url: '/driver',
              tag: `delivery-${deliveryId}`,
            },
          });
        } catch (pushError) {
          console.warn('[Logistics] Erro ao enviar push notification:', pushError);
        }
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliveries'] });
      queryClient.invalidateQueries({ queryKey: ['logistics-technicians'] });
      toast({
        title: 'Entrega atribuída',
        description: 'O técnico logístico foi notificado.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao atribuir entrega',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Update delivery status with optimistic updates
  const updateDeliveryStatus = useMutation({
    mutationFn: async ({ 
      deliveryId, 
      status, 
      notes 
    }: { 
      deliveryId: string; 
      status: DeliveryStatus;
      notes?: string;
    }) => {
      const updateData: any = { status };

      const { data: existing } = await supabase
        .from('deliveries')
        .select('started_at, delivered_at, status, origin_type, origin_id')
        .eq('id', deliveryId)
        .single();

      const previousStatus = existing?.status;

      if (status === 'in_transit' && !existing?.started_at) {
        updateData.started_at = new Date().toISOString();
      } else if (status === 'delivered' && !existing?.delivered_at) {
        updateData.delivered_at = new Date().toISOString();
      }

      // L-LOC.2: coleta de locação — a RPC confirma PRIMEIRO (devolve estoque, libera a unidade,
      // fecha a locação e marca a entrega como delivered, tudo no banco). Só depois a entrega é
      // gravada aqui. Falha da RPC aborta a mutation: a entrega NÃO fica delivered com a locação
      // aberta (onError reverte o cache otimista e avisa). A RPC é idempotente: se a coleta já
      // estava confirmada devolve already_confirmed=true, que não é erro — segue normalmente.
      if (status === 'delivered' && existing?.origin_type === 'rental_collection' && existing?.origin_id) {
        const { error: confirmError } = await (supabase.rpc as any)(
          'confirm_rental_collection',
          {
            p_delivery_id: deliveryId,
            p_rental_id:   existing.origin_id,
          }
        );
        if (confirmError) {
          console.error(
            `[Logistics] confirm_rental_collection error (delivery=${deliveryId}):`,
            confirmError
          );
          throw confirmError;
        }
      }

      const { data: delivery, error } = await supabase
        .from('deliveries')
        .update(updateData)
        .eq('id', deliveryId)
        .select()
        .single();

      if (error) throw error;

      // Falha ruidosa: histórico é trilha de auditoria da entrega — se o INSERT
      // falhar, avisar o usuário sem reverter o status já gravado.
      const { error: historyError } = await supabase.from('delivery_history').insert({
        delivery_id: deliveryId,
        action: 'STATUS_CHANGED',
        previous_status: previousStatus,
        new_status: status,
        changed_by: user?.id,
        notes,
      });
      if (historyError) {
        console.error(
          `[Logistics] Falha ao registrar delivery_history (delivery=${deliveryId}, status=${status}):`,
          historyError
        );
        toast({
          title: 'Histórico não registrado',
          description: 'Entrega atualizada, mas o histórico não foi registrado. Informe o suporte.',
          variant: 'destructive',
        });
      }

      const auditDescriptions: Record<DeliveryStatus, string> = {
        pending: 'Entrega marcada como pendente',
        assigned: 'Entrega atribuída',
        in_transit: 'Entrega iniciada - Em trânsito',
        delivered: 'Entrega concluída com sucesso',
        failed: 'Entrega marcada como falha',
        cancelled: 'Entrega cancelada',
      };

      await auditService.logAction({
        action: 'ATUALIZAÇÃO',
        module: 'LOGÍSTICA',
        entityType: 'ENTREGA',
        entityId: deliveryId,
        description: auditDescriptions[status],
        details: {
          previous_status: previousStatus,
          new_status: status,
          timestamp: new Date().toISOString(),
          notes,
        },
      });

      return delivery;
    },
    onMutate: async ({ deliveryId, status }) => {
      await queryClient.cancelQueries({ queryKey: ['deliveries'] });
      
      const previousDeliveries = queryClient.getQueriesData<DeliveryWithDetails[]>({ 
        queryKey: ['deliveries'] 
      });
      
      queryClient.setQueriesData<DeliveryWithDetails[]>(
        { queryKey: ['deliveries'] },
        (old) => {
          if (!old) return old;
          return old.map(d => 
            d.id === deliveryId 
              ? { 
                  ...d, 
                  status, 
                  delivered_at: status === 'delivered' && !d.delivered_at ? new Date().toISOString() : d.delivered_at,
                  started_at: status === 'in_transit' && !d.started_at ? new Date().toISOString() : d.started_at,
                }
              : d
          );
        }
      );
      
      return { previousDeliveries };
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['deliveries'] });
      // Se era coleta de locação confirmada, invalida queries do módulo de locações
      if (variables.status === 'delivered') {
        queryClient.invalidateQueries({ queryKey: ['rentals'] });
        queryClient.invalidateQueries({ queryKey: ['rentals-stats'] });
        queryClient.invalidateQueries({ queryKey: ['rental-history-events'] });
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
      }
      toast({
        title: 'Status atualizado',
        description: 'O status da entrega foi atualizado.',
      });
    },
    onError: (error: Error, _variables, context) => {
      if (context?.previousDeliveries) {
        context.previousDeliveries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      toast({
        title: 'Erro ao atualizar status',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Delete delivery — CASCADE handles dependent records
  const deleteDelivery = useMutation({
    mutationFn: async (deliveryId: string) => {
      // Verificar status antes de excluir
      const { data: delivery } = await supabase
        .from('deliveries')
        .select('status')
        .eq('id', deliveryId)
        .single();

      if (delivery?.status === 'in_transit') {
        throw new Error('Não é possível excluir uma entrega em trânsito. Cancele-a primeiro.');
      }
      if (delivery?.status === 'delivered') {
        throw new Error('Não é possível excluir uma entrega já finalizada.');
      }

      // Deletar entrega principal — ON DELETE CASCADE cuida dos dependentes
      const { data, error } = await supabase
        .from('deliveries')
        .delete()
        .eq('id', deliveryId)
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Entrega não encontrada ou já excluída.');
      
      return { deleted: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliveries'] });
      toast({
        title: 'Entrega excluída',
        description: 'A entrega foi excluída com sucesso.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao excluir entrega',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Unassign delivery (return to pool)
  const unassignDelivery = useMutation({
    mutationFn: async (deliveryId: string) => {
      const { data, error } = await supabase
        .from('deliveries')
        .update({
          assigned_to: null,
          assigned_at: null,
          status: 'pending' as DeliveryStatus,
        })
        .eq('id', deliveryId)
        .select()
        .single();

      if (error) throw error;

      await supabase.from('delivery_history').insert({
        delivery_id: deliveryId,
        action: 'UNASSIGNED',
        new_status: 'pending',
        changed_by: user?.id,
        notes: 'Entrega devolvida ao pool geral',
      });

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliveries'] });
      queryClient.invalidateQueries({ queryKey: ['logistics-technicians'] });
      toast({
        title: 'Entrega devolvida',
        description: 'A entrega voltou para o pool geral.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao devolver entrega',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    createDelivery: createDelivery.mutateAsync,
    assignDelivery: assignDelivery.mutateAsync,
    updateDeliveryStatus: updateDeliveryStatus.mutateAsync,
    deleteDelivery: deleteDelivery.mutateAsync,
    unassignDelivery: unassignDelivery.mutateAsync,
    isCreating: createDelivery.isPending,
    isAssigning: assignDelivery.isPending,
    isUpdating: updateDeliveryStatus.isPending,
    isDeleting: deleteDelivery.isPending,
    isUnassigning: unassignDelivery.isPending,
  };
};
